import { DealerContextDropdownMenu } from "@components/DealerContextSelector/DealerContextDropdownMenu";
import ArrowIcon from "@lyteflo/assets/icons/symbols/arrow.svg?react";
import type { DealerGroup } from "@lyteflo/types";
import {
  computeDealerContextSections,
  DEALER_CONTEXT_TYPE,
  type DealerContext,
  hasGroupRole,
  hasLytefloRole,
  useClickOutside,
  useProfile,
} from "@lyteflo/utils";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "preact/compat";
import { useTranslation } from "react-i18next";

interface DealerContextDropdownProps {
  className?: string;
  inputClassName?: string;
  menuClassName?: string;
  isLoading?: boolean;
  placeholder?: string;
  label?: string;
  dealerGroups: DealerGroup[];
  selectedKey?: string;
  selectedLabel: string;
  onSelect: (next: DealerContext) => void;
  onOpenChange?: (open: boolean) => void;
  showAllOption?: boolean;
  showSectionCaptions?: boolean;
}

export const DealerContextDropdown = ({
  className = "",
  inputClassName = "",
  menuClassName = "",
  isLoading,
  placeholder,
  label,
  dealerGroups,
  selectedKey,
  selectedLabel,
  onSelect,
  onOpenChange,
  showAllOption = false,
  showSectionCaptions = true,
}: DealerContextDropdownProps) => {
  const { t } = useTranslation();
  const { profile } = useProfile();
  const containerRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const resolvedShowAllOption = profile
    ? hasLytefloRole(profile) && showAllOption
    : false;
  const isGroupRole = profile ? hasGroupRole(profile) : false;

  const sections = useMemo(
    () =>
      computeDealerContextSections({
        profile,
        dealerGroups,
        searchTerm: searchQuery,
        showAllOption: resolvedShowAllOption,
        isGroupRole,
        t,
      }).map((s) => (showSectionCaptions ? s : { ...s, headerLabel: null })),
    [
      profile,
      dealerGroups,
      searchQuery,
      resolvedShowAllOption,
      isGroupRole,
      showSectionCaptions,
      t,
    ],
  );

  const hasResults = sections.some((s) => s.options.length > 0);

  const close = useCallback(() => {
    setOpen(false);
    setSearchQuery("");
    onOpenChange?.(false);
  }, [onOpenChange]);

  const toggleOpen = () => {
    const next = !open;
    if (next) setSearchQuery("");
    setOpen(next);
    onOpenChange?.(next);
  };

  const handleClickOutside = useCallback(() => close(), [close]);
  useClickOutside([containerRef], handleClickOutside);

  useEffect(() => {
    if (!open || !selectedKey || !menuRef.current) return;
    if (selectedKey === DEALER_CONTEXT_TYPE.ALL) return;
    const target = menuRef.current.querySelector(
      `[data-context-key="${selectedKey}"]`,
    ) as HTMLElement | null;
    target?.scrollIntoView({ block: "nearest" });
  }, [open, selectedKey]);

  const handleOptionSelect = (next: DealerContext) => {
    onSelect(next);
    close();
  };

  const inputValue =
    searchQuery || (!open && selectedLabel ? selectedLabel : "");

  return (
    <div ref={containerRef} className={`lf-relative lf-w-full ${className}`}>
      {label && (
        <div className="lf-mb-1 lf-text-xs lf-font-medium lf-text-[var(--gray-11)]">
          {label}
        </div>
      )}
      <div className="lf-relative lf-flex lf-items-center lf-rounded lf-border lf-border-[var(--gray-7)] lf-bg-[var(--gray-1)] lf-px-3 lf-py-1.5 lf-text-sm focus-within:lf-ring-2 focus-within:lf-ring-[var(--focus-8)]">
        <input
          type="text"
          className={`lf-min-w-0 lf-flex-1 lf-border-none lf-bg-transparent lf-text-ellipsis lf-text-[var(--gray-12)] lf-outline-none ${inputClassName}`}
          value={inputValue}
          onChange={(e) => {
            const value = (e.target as HTMLInputElement).value;
            setSearchQuery(value);
            if (!open) {
              setOpen(true);
              onOpenChange?.(true);
            }
          }}
          onFocus={() => {
            if (!open) toggleOpen();
          }}
          placeholder={placeholder || ""}
        />
        <button
          type="button"
          className="lf-ml-2 lf-flex lf-shrink-0 lf-items-center lf-justify-center"
          onClick={toggleOpen}
          tabIndex={-1}
        >
          <ArrowIcon
            className={`lf-h-4 lf-w-4 lf-shrink-0 lf-transition-transform ${
              open ? "lf-rotate-180" : ""
            }`}
          />
        </button>
      </div>
      {open && (
        <div
          ref={menuRef}
          className={`lf-absolute lf-z-[2147483650] lf-w-full lf-rounded-md lf-border lf-border-[var(--gray-3)] lf-bg-panel-default lf-shadow-lg ${menuClassName}`}
        >
          <DealerContextDropdownMenu
            sections={sections}
            selectedKey={selectedKey}
            isLoading={isLoading}
            onOptionSelect={handleOptionSelect}
            t={t}
            hasResults={hasResults}
          />
        </div>
      )}
    </div>
  );
};
